/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance"; // token JWT incluido automáticamente
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
} from "recharts";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";

const NewStopSell = () => {
  const { baseCurrency } = useCurrencyDisplay();
  const [searchParams] = useSearchParams();
  const businessUnitId = searchParams.get("business_unit_id") || "1";
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    room_rate_id: "",
    start_date: "",
    end_date: "",
  });

  const [roomRates, setRoomRates] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [stopSells, setStopSells] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [hoveredStopSellDate, setHoveredStopSellDate] = useState(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/roomRates/all`)
      .then((response) => {
        const filtered = response.data.filter(
          (rate) => rate.room_category?.business_unit_id == businessUnitId
        );
        setRoomRates(filtered);
      })
      .catch((error) => {
        console.error("❌ Error al cargar room rates:", error);
      });
  }, [businessUnitId]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/stop-sells`)
      .then((response) => {
        const filtered = response.data.filter(
          (ss) => ss.room_rate?.room_category?.business_unit_id == businessUnitId
        );
        setStopSells(filtered);
      })
      .catch((error) => {
        console.error("❌ Error al cargar stop sells:", error);
      });
  }, [businessUnitId]);

  useEffect(() => {
    if (!formData.room_rate_id) {
      setPriceHistory([]);
      setChartData([]);
      return;
    }

    axios.get(`${process.env.REACT_APP_API_URL}/api/rate-prices/history/${formData.room_rate_id}`)
      .then((res) => setPriceHistory(res.data))
      .catch((err) => console.error("❌ Error al cargar historial de precios:", err));
  }, [formData.room_rate_id]);

  useEffect(() => {
    const today = new Date();
    const futureDates = [];

    for (let i = 0; i < 365; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const formatted = date.toISOString().split("T")[0];
      futureDates.push({ date: formatted });
    }

    const staysGrouped = {};
    const datesSet = new Set();

    futureDates.forEach(({ date }) => {
      datesSet.add(date);
      priceHistory.forEach((entry) => {
        if (date >= entry.start_date && date <= entry.end_date) {
          const key =
            entry.min_stay == null && entry.max_stay == null
              ? "null"
              : `${entry.min_stay ?? 0} / ${entry.max_stay ?? "∞"}`;

          if (!staysGrouped[key]) {
            staysGrouped[key] = {};
          }

          staysGrouped[key][date] = parseFloat(entry.price);
        }
      });
    });

    const combined = Array.from(datesSet).sort().map((date) => {
      const point = { date };
      for (const group in staysGrouped) {
        point[group] = staysGrouped[group][date] ?? null;
      }
      return point;
    });

    setChartData(combined);
  }, [priceHistory]);

  const validateForm = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(`${formData.start_date}T00:00:00`);
    const end = new Date(`${formData.end_date}T00:00:00`);

    if (isNaN(start) || isNaN(end)) {
      alert("Las fechas introducidas no son válidas.");
      return false;
    }

    if (start < today) {
      alert("Start date no puede ser anterior a hoy.");
      return false;
    }

    if (end < start) {
      alert("End date no puede ser anterior a Start date.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/stop-sells`, formData);
      alert("Stop Sell added");
      navigate(`/revenue-management?business_unit_id=${businessUnitId}`);
    } catch (error) {
      console.error("❌ Error al crear Stop Sell:", error);
      alert("Error al crear Stop Sell");
    }
  };


  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const isStopSell = stopSells.some(
        (ss) =>
          ss.room_rate_id == formData.room_rate_id &&
          label >= ss.start_date &&
          label <= ss.end_date
      );
  
      return (
        <div className="bg-dark text-light p-2 border border-secondary rounded">
          <p className="mb-2">📅 <strong>{label}</strong></p>
          {payload.map((item, index) => (
            <div key={index} className="mb-1">
              <span style={{ color: item.color }}>⬤</span>{" "}
              <strong>{item.name}</strong>: {item.value} {baseCurrency}
            </div>
          ))}
          {isStopSell && (
            <div className="mt-2 text-warning fw-bold">
              🛑 STOP SELL ACTIVE
              {stopSells
                .filter(
                  (ss) =>
                    ss.room_rate_id == formData.room_rate_id &&
                    label >= ss.start_date &&
                    label <= ss.end_date
                )
                .map((ss, idx) => (
                  <div key={idx} className="fw-normal text-white">
                    {ss.start_date} → {ss.end_date}
                  </div>
                ))}
            </div>
          )}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="p-4 d-flex justify-content-center">
      <div className="card bg-dark text-light w-100 p-4 d-flex flex-row">
        {/* Formulario a la izquierda */}
        <form className="pe-4" style={{ width: "40%" }} onSubmit={handleSubmit}>
          <h2 className="fs-5 text-center mb-3 fw-bold">Create Stop Sell</h2>

          <div className="mb-3">
            <label className="form-label">Room Rate</label>
            <select
              name="room_rate_id"
              value={formData.room_rate_id}
              onChange={(e) => setFormData({ ...formData, room_rate_id: e.target.value })}
              className="form-select form-select-sm p-1"
              required
            >
              <option value="">Select a Rate</option>
              {roomRates.map((rate) => (
                <option key={rate.id} value={rate.id}>
                  {rate.name} ({rate.rate_code})
                </option>
              ))}
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">Start Date</label>
            <input
              type="date"
              className="form-control"
              name="start_date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">End Date</label>
            <input
              type="date"
              className="form-control"
              name="end_date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              required
            />
          </div>

          <div className="text-center mt-4 mb-2">
            <button
              type="submit"
              className="btn text-white fw-semibold px-4"
              style={{
                background: "linear-gradient(90deg, #D4AF37, #FF4500)",
                border: "none",
              }}
            >
              Add Stop Sell
            </button>
          </div>
        </form>

        {/* Gráfico a la derecha */}
        {chartData.length > 0 && (
          <div style={{ width: "60%" }}>
            <h4 className="text-white mb-3">Rate next year</h4>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={chartData}
                onMouseMove={(e) => {
                  if (e && e.activeLabel) {
                    const hoveredDate = e.activeLabel;
                    const matching = stopSells.find(
                      (ss) =>
                        hoveredDate >= ss.start_date &&
                        hoveredDate <= ss.end_date &&
                        ss.room_rate_id == formData.room_rate_id
                    );
                    if (matching) {
                      setHoveredStopSellDate({
                        date: hoveredDate,
                        range: `${matching.start_date} → ${matching.end_date}`,
                      });
                    } else {
                      setHoveredStopSellDate(null);
                    }
                  }
                }}
                onMouseLeave={() => setHoveredStopSellDate(null)}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#555" />
                <XAxis dataKey="date" stroke="#aaa" />
                <YAxis stroke="#aaa" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {stopSells
                  .filter((ss) => ss.room_rate_id == formData.room_rate_id)
                  .map((ss, index) => (
                    <ReferenceArea
                      key={`stop-sell-${index}`}
                      x1={ss.start_date}
                      x2={ss.end_date}
                      stroke="gold"
                      strokeOpacity={0}
                      fill="yellow"
                      fillOpacity={0.3}
                      label={{
                        value: "STOP SELL",
                        position: "top",
                        fill: "#ccc",
                        fontSize: 10,
                      }}
                    />
                  ))}
                {Object.keys(chartData[0])
                  .filter((key) => key !== "date")
                  .map((key, index) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      strokeWidth={2}
                      stroke={`hsl(${(index * 60) % 360}, 70%, 50%)`}
                      name={`Stay (min/max): ${key}`}
                      dot={false}
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewStopSell;
